import { useTranslations } from 'next-intl';
import React, { useState, useMemo } from 'react';
import { type UserT } from '@mentimeter/user';
import {
  PlanKeyEnum,
  type TrackingContext,
  type SubscriptionsResponseT,
  WorkspaceRoleEnum,
} from '@mentimeter/http-clients';
import { usePathname, useRouter } from '@mentimeter/next-navigation';
import { trackUser } from '@api/tracking/client';
import { isSafeUrl } from '@mentimeter/auth';
import { type GetInvitesResponse } from '@core-api/workspace-members/workspace-members/invites/{token}';
import { trackDropOutReason } from '../join-page/utils';
import { InviteError } from '../../errors/InviteError';
import {
  JoinErrorEnum,
  useGetErrorMessage,
} from '../join-page/handleErrorMessage';
import { JoinTeamModalContent } from './JoinTeamModalContent';
import { type AuthStateT, type JoinModeT } from './utils';
import { useConsumeToken } from './use-consume-token';
import type { ConsumeStatus } from './types';

export const JoinTeamModalContainer = ({
  token,
  authState,
  joinMode,
  inviteSeriesId,
  user,
  subscriptions,
  trackingContext,
  continuePath,
  inviteDetails,
  closeModal,
}: {
  token: string;
  authState: AuthStateT;
  joinMode: JoinModeT;
  inviteSeriesId: string | undefined;
  user: UserT;
  subscriptions: SubscriptionsResponseT | undefined;
  trackingContext: TrackingContext;
  continuePath?: string;
  inviteDetails: GetInvitesResponse;
  closeModal: () => void;
}) => {
  const t = useTranslations('onboarding.join_workspace');
  const [hasConsumedToken, setHasConsumedToken] = useState(false);
  const joinError = useMemo(
    () => checkUserEligableToJoinWorkspace(user, subscriptions, inviteDetails),
    [user, subscriptions, inviteDetails],
  );
  const router = useRouter();
  const pathname = usePathname();
  const getErrorMessage = useGetErrorMessage();

  const {
    isMutating: isConsumingToken,
    trigger: consumeToken,
    error: consumeTokenError,
  } = useConsumeToken(token, {
    onError: (error) => {
      const errorMessage = getErrorMessage({
        workspaceName: inviteDetails?.workspaceName || 'Workspace',
        error: error.code,
      });

      trackDropOutReason({
        reason: errorMessage.trackingDropoutReason,
        userId: user.id,
        teamName: inviteDetails?.workspaceName || 'Workspace',
        joinMode,
        authState,
        path: window.location.pathname,
      });
    },
    onSuccess: () => {
      setHasConsumedToken(true);

      trackConfirmationAccepted({
        plan: inviteDetails.workspacePlanFeatures,
        role: inviteDetails.invitedRole,
        activeTeamMembers: inviteDetails.workspaceMembersCount,
        context: trackingContext,
      });
    },
  });

  const consumeStatus: ConsumeStatus = useMemo(() => {
    if (hasConsumedToken) return { state: 'success' };
    if (consumeTokenError) return { error: consumeTokenError, state: 'error' };
    if (joinError) return { error: joinError, state: 'error' };

    return { state: 'pending-consent' };
  }, [joinError, consumeTokenError, hasConsumedToken]);

  const successActionText = inviteSeriesId
    ? t('start_collaborating_button')
    : t('start_exploring_button');

  return (
    <JoinTeamModalContent
      isLoading={isConsumingToken}
      consumeStatus={consumeStatus}
      user={user}
      inviteDetails={inviteDetails}
      teamName={inviteDetails.workspaceName || 'Workspace'}
      successActionText={successActionText}
      onDismiss={onDismiss}
      onJoinWorkspaceClick={consumeToken}
      onSuccessActionClick={completeJoin}
    />
  );

  function completeJoin() {
    closeModal();

    if (continuePath && isSafeUrl(continuePath)) {
      router.replace(continuePath);
    } else {
      router.replace(pathname); //reset query params
    }
  }

  function onDismiss() {
    closeModal();

    trackDropOutReason({
      reason: 'clicked cancel',
      teamName: inviteDetails.workspaceName || 'Workspace',
      userId: user.id,
      joinMode,
      authState,
      path: window.location.pathname,
    });
  }
};

function trackConfirmationAccepted({
  context,
  plan,
  role,
  activeTeamMembers,
}: {
  context: TrackingContext;
  plan: string;
  role: GetInvitesResponse['invitedRole'];
  activeTeamMembers: number;
}) {
  trackUser({
    event: 'Joined workspace through invite',
    properties: {
      context,
      'active team members': activeTeamMembers,
      'current plan': plan,
      'role in workspace': role,
    },
  });
}

const checkUserEligableToJoinWorkspace = (
  user: UserT,
  subscription: SubscriptionsResponseT | undefined,
  inviteDetails: GetInvitesResponse,
): InviteError | undefined => {
  const isMemberOfTeam =
    (subscription?.current_subscription?.licenses ?? 0) > 1;
  if (user.role === WorkspaceRoleEnum.OWNER && isMemberOfTeam) {
    return new InviteError(JoinErrorEnum.USER_IS_TEAM_OWNER);
  }

  const isEnterpriseUser =
    user.features.type === 'enterprise' ||
    subscription?.current_subscription?.plan.key ===
      PlanKeyEnum.ENTERPRISE_TRIAL;

  if (isEnterpriseUser) {
    return new InviteError(JoinErrorEnum.USER_IS_ENTERPRISE_MEMBER);
  }

  if (isMemberOfTeam) {
    return new InviteError(JoinErrorEnum.USER_IS_TEAM_MEMBER);
  }

  const blockPaidUserJoinFreeWorkspace =
    !user.isFreeUser && inviteDetails.workspacePlanFeatures === 'registered';

  if (blockPaidUserJoinFreeWorkspace) {
    return new InviteError(JoinErrorEnum.USER_HAS_OTHER_SUBSCRIPTION);
  }
  return;
};
